import { useEffect, useRef, useState } from 'react';
import { WeatherSettings } from './WeatherControls';

interface AudioManagerProps {
  settings: WeatherSettings;
  lightningTrigger: boolean;
  masterVolume: number;
  rainVolume: number;
  thunderVolume: number;
  backgroundMusicVolume: number;
  onAudioReady: (canPlay: boolean) => void;
}

export const AudioManager = ({
  settings,
  lightningTrigger,
  masterVolume,
  rainVolume,
  thunderVolume,
  backgroundMusicVolume,
  onAudioReady
}: AudioManagerProps) => {
  const rainAudioRef = useRef<HTMLAudioElement>(null);
  const thunderAudioRef = useRef<HTMLAudioElement>(null);
  const thunder2AudioRef = useRef<HTMLAudioElement>(null);
  const thunder3AudioRef = useRef<HTMLAudioElement>(null);
  const backgroundMusicRef = useRef<HTMLAudioElement>(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [backgroundMusicPlaying, setBackgroundMusicPlaying] = useState(false);
  const lastLightningRef = useRef(false);

  // Initialize audio when component mounts
  useEffect(() => {
    const initAudio = async () => {
      try {
        // Try to play background music automatically
        if (backgroundMusicRef.current) {
          await backgroundMusicRef.current.play();
          setBackgroundMusicPlaying(true);
          setAudioEnabled(true);
          onAudioReady(true);
        }
      } catch (error) {
        console.log('Autoplay blocked - user interaction required');
        onAudioReady(false);
      }
    };

    initAudio();
  }, [onAudioReady]);

  // Handle user interaction to enable audio
  const enableAudio = async () => {
    try {
      if (backgroundMusicRef.current && !backgroundMusicPlaying) {
        await backgroundMusicRef.current.play();
        setBackgroundMusicPlaying(true);
      }
      setAudioEnabled(true);
      onAudioReady(true);
    } catch (error) {
      console.error('Failed to enable audio:', error);
    }
  };

  // Add click listener to enable audio on first interaction
  useEffect(() => {
    if (!audioEnabled) {
      const handleClick = () => {
        enableAudio();
        document.removeEventListener('click', handleClick);
      };
      
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [audioEnabled]);

  // Update audio volumes
  useEffect(() => {
    const masterVol = masterVolume / 100;
    
    if (rainAudioRef.current) {
      rainAudioRef.current.volume = (rainVolume / 100) * masterVol;
    }
    
    if (thunderAudioRef.current) {
      thunderAudioRef.current.volume = (thunderVolume / 100) * masterVol;
    }
    
    if (thunder2AudioRef.current) {
      thunder2AudioRef.current.volume = (thunderVolume / 100) * masterVol;
    }
    
    if (thunder3AudioRef.current) {
      thunder3AudioRef.current.volume = (thunderVolume / 100) * masterVol;
    }
    
    if (backgroundMusicRef.current) {
      backgroundMusicRef.current.volume = (backgroundMusicVolume / 100) * masterVol;
    }
  }, [masterVolume, rainVolume, thunderVolume, backgroundMusicVolume]);

  // Handle rain audio based on intensity
  useEffect(() => {
    if (!audioEnabled || !rainAudioRef.current) return;

    const rainAudio = rainAudioRef.current;
    const shouldPlayRain = settings.rainIntensity > 10;

    if (shouldPlayRain) {
      if (rainAudio.paused) {
        rainAudio.play().catch(console.error);
      }
      // Adjust playback rate based on rain speed
      rainAudio.playbackRate = 0.8 + (settings.rainSpeed / 100) * 0.4;
    } else {
      if (!rainAudio.paused) {
        rainAudio.pause();
      }
    }
  }, [settings.rainIntensity, settings.rainSpeed, audioEnabled]);

  // Handle thunder audio based on lightning with realistic timing
  useEffect(() => {
    if (!audioEnabled) return;

    if (lightningTrigger && !lastLightningRef.current) {
      // Randomly select one of the thunder sounds for variety
      const thunderSounds = [thunderAudioRef.current, thunder2AudioRef.current, thunder3AudioRef.current];
      const selectedThunder = thunderSounds[Math.floor(Math.random() * thunderSounds.length)];
      
      if (selectedThunder) {
        // Realistic thunder timing based on lightning intensity
        // Higher intensity = closer lightning = less delay
        const maxDelay = Math.max(0.5, 4 - (settings.lightningIntensity / 100) * 3);
        const delay = Math.random() * maxDelay * 1000;
        
        setTimeout(() => {
          if (selectedThunder && !selectedThunder.paused) {
            selectedThunder.pause();
          }
          selectedThunder.currentTime = 0;
          selectedThunder.play().catch(console.error);
          
          // Add slight volume variation for realism
          const volumeVariation = 0.8 + Math.random() * 0.4;
          selectedThunder.volume = (thunderVolume / 100) * (masterVolume / 100) * volumeVariation;
        }, delay);
      }
    }
    
    lastLightningRef.current = lightningTrigger;
  }, [lightningTrigger, audioEnabled, settings.lightningIntensity, thunderVolume, masterVolume]);

  // Handle background music playback
  useEffect(() => {
    if (!audioEnabled || !backgroundMusicRef.current) return;

    const backgroundMusic = backgroundMusicRef.current;
    
    if (backgroundMusicVolume > 0) {
      if (backgroundMusic.paused) {
        backgroundMusic.play().catch(console.error);
        setBackgroundMusicPlaying(true);
      }
    } else {
      if (!backgroundMusic.paused) {
        backgroundMusic.pause();
        setBackgroundMusicPlaying(false);
      }
    }
  }, [backgroundMusicVolume, audioEnabled]);

  return (
    <>
      {/* Rain Audio */}
      <audio
        ref={rainAudioRef}
        loop
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="https://archive.org/download/RainThunder/Rain.mp3" type="audio/mpeg" />
        <source src="https://www.soundjay.com/misc/sounds/rain-01.wav" type="audio/wav" />
      </audio>

      {/* Thunder Audio - Multiple variations for realism */}
      <audio
        ref={thunderAudioRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="https://archive.org/download/RainThunder/Thunder1.mp3" type="audio/mpeg" />
        <source src="https://www.soundjay.com/misc/sounds/thunder-01.wav" type="audio/wav" />
      </audio>

      <audio
        ref={thunder2AudioRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="https://archive.org/download/RainThunder/Thunder2.mp3" type="audio/mpeg" />
        <source src="https://www.soundjay.com/misc/sounds/thunder-02.wav" type="audio/wav" />
      </audio>

      <audio
        ref={thunder3AudioRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="https://archive.org/download/RainThunder/Thunder3.mp3" type="audio/mpeg" />
        <source src="https://www.soundjay.com/misc/sounds/thunder-03.wav" type="audio/wav" />
      </audio>

      {/* Background Music */}
      <audio
        ref={backgroundMusicRef}
        loop
        preload="auto"
        style={{ display: 'none' }}
        onLoadedData={() => console.log('Background music loaded')}
        onError={(e) => console.error('Background music error:', e)}
      >
        <source src="https://raw.githubusercontent.com/Geek4IT/vibecode/main/thunder-156423.mp3" type="audio/mpeg" />
      </audio>

      {/* Audio Enable Prompt */}
      {!audioEnabled && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg text-center max-w-md">
            <h3 className="text-lg font-semibold mb-4">🌩️ Enable Thunder Experience</h3>
            <p className="text-gray-600 mb-4">
              Click to enable immersive storm sounds with realistic thunder, rain, and atmospheric music
            </p>
            <div className="text-sm text-gray-500 mb-4">
              ⚡ Dynamic thunder variations<br/>
              🌧️ Realistic rain audio<br/>
              🎵 Atmospheric background music
            </div>
            <button
              onClick={enableAudio}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              🔊 Enable Thunder & Audio
            </button>
          </div>
        </div>
      )}
    </>
  );
};